/**
 * The platform's model settings (F-GEN-036, MS-07 contract): `GET` and `PUT
 * /api/admin/model-settings`, `POST /api/admin/model-settings/restore-env` and
 * `GET /api/admin/model-settings/revisions`.
 *
 * Money is integer micro-dollars per million tokens. `null` on a cap means the application
 * imposes none (an unset output cap sends no `max_tokens`, an unset message maximum accepts any
 * message under the HTTP body limit). The `PUT` and the restore answer the new `GET` shape.
 */

export type ReasoningEffort = "minimal" | "low" | "medium" | "high";

export const REASONING_EFFORTS: readonly ReasoningEffort[] = ["minimal", "low", "medium", "high"];

export type TierName = "fast" | "capable";

export interface TierSettings {
  readonly model_id: string;
  readonly max_output_tokens: number | null;
  readonly reasoning_effort: ReasoningEffort | null;
}

export interface PriceEntry {
  readonly prompt_micros_per_million_tokens: number;
  readonly completion_micros_per_million_tokens: number;
  readonly currency: string;
}

export type SettingsSource = "env" | "panel";

/** What the admin may change, and the whole of a `PUT` body beside `expected_revision`. */
export interface EditableSettings {
  readonly tiers: Readonly<Record<TierName, TierSettings>>;
  readonly prices: Readonly<Record<string, PriceEntry>>;
  readonly message_max_chars: number | null;
}

export interface ModelSettingsRequest extends EditableSettings {
  readonly expected_revision: number;
}

/** The body of the estimate preview: a `PUT` body without the revision (nothing is written). */
export type EstimateRequest = EditableSettings;

/** What the most one turn can cost under the previewed values, or `null` when it cannot be computed. */
export interface EstimateView {
  readonly estimated_max_cost_micros_per_turn: number | null;
}

export interface ModelSettingsView extends EditableSettings {
  readonly revision: number;
  readonly source: SettingsSource;
  readonly updated_at: string | null;
  readonly updated_by_name: string | null;
  /** Read-only: they come from the environment and are not editable here. */
  readonly technical_limits: {
    readonly call_deadline_seconds: number;
    readonly http_max_body_bytes: number;
  };
  /** The server's estimate of the most one turn can cost with the stored settings, or `null`. */
  readonly estimated_max_cost_micros_per_turn: number | null;
}

export interface RestoreEnvRequest {
  readonly expected_revision: number;
}

export interface RevisionItem {
  readonly revision: number;
  readonly source: SettingsSource;
  readonly created_at: string;
  readonly author_name: string | null;
  readonly changed_fields: readonly string[];
}

export interface RevisionsView {
  readonly items: readonly RevisionItem[];
}
